/**
 * Keeping a customer-care list in step with the one record that just changed,
 * without reading the whole list again (Reviews, Questions, Support).
 */

/**
 * A moderated review or question that leaves the statuses being shown leaves
 * the list — and the count with it; anywhere else it updates in place.
 */
export function settleModerated<T extends { id: string; status: string }>(
  rows: T[],
  total: number | null,
  shown: readonly string[],
  updated: T,
): { items: T[]; total: number | null } {
  if (shown.length > 0 && !shown.includes(updated.status)) {
    const items = rows.filter((row) => row.id !== updated.id);
    const removed = items.length !== rows.length;
    return { items, total: total !== null && removed ? Math.max(0, total - 1) : total };
  }
  return { items: rows.map((row) => (row.id === updated.id ? updated : row)), total };
}

/**
 * The support queue's row for a ticket that just changed. The queue runs most
 * recently active first, so an answered ticket goes to the top — where a
 * reload would put it — while a hand-off or an internal note leaves it where
 * the agent's eye already is. A row that no longer matches the filter stays
 * until the next load: the ticket just worked should not vanish from under
 * the agent.
 */
export function settleTicket<T extends { id: string; lastReplyAt: string }>(rows: T[], updated: T): T[] {
  const before = rows.find((row) => row.id === updated.id);
  if (!before) return rows;

  return before.lastReplyAt === updated.lastReplyAt
    ? rows.map((row) => (row.id === updated.id ? updated : row))
    : [updated, ...rows.filter((row) => row.id !== updated.id)];
}
